// src/pages/Public/Volunteer/PublicEventCalendar.js

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Chip, Grid, IconButton, CircularProgress, Alert, Tooltip,
  ToggleButton, ToggleButtonGroup, Container
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon, ArrowForward as ArrowForwardIcon,
  Today as TodayIcon, LocationOn as LocationIcon, Event as EventIcon,
  AccessTime as TimeIcon, People as PeopleIcon, Close as CloseIcon,
  ViewModule as MonthIcon, ViewWeek as WeekIcon, ViewDay as DayIcon
} from '@mui/icons-material';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, addMonths, subMonths, addWeeks, subWeeks, addDays, subDays } from 'date-fns';
import enUS from 'date-fns/locale/en-US';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import backendURL from '../../../config';

const locales = { 'en-US': enUS };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const eventTypeColors = {
  RALLY: { bg: '#e3f2fd', border: '#1976d2', text: '#1565c0' },
  DONATION: { bg: '#e8f5e9', border: '#4caf50', text: '#2e7d32' },
  FUNDRAISING: { bg: '#fff3e0', border: '#ff9800', text: '#e65100' },
  COMMUNITY_SERVICE: { bg: '#f3e5f5', border: '#9c27b0', text: '#7b1fa2' },
  OTHER: { bg: '#eceff1', border: '#607d8b', text: '#455a64' }
};

const fillStatusColors = {
  open: { bg: '#c8e6c9', text: '#2e7d32' },
  partial: { bg: '#fff9c4', text: '#f57f17' },
  full: { bg: '#ffcdd2', text: '#c62828' }
};

const PublicEventCalendar = () => {
  const { chatbotId } = useParams();
  const [searchParams] = useSearchParams();
  
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState(searchParams.get('view') || 'month');
  const [chatbotName, setChatbotName] = useState('Events');
  
  // Dialog
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDetailDialog, setEventDetailDialog] = useState(false);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      let start, end;
      if (view === 'month') {
        start = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        end = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      } else if (view === 'week') {
        start = startOfWeek(currentDate);
        end = new Date(start);
        end.setDate(end.getDate() + 6);
      } else {
        start = currentDate;
        end = currentDate;
      }
      
      start = subDays(start, 7);
      end = addDays(end, 7);
      
      const response = await axios.get(
        `${backendURL}/api/public/events/calendar/${chatbotId}`,
        {
          params: {
            start: format(start, 'yyyy-MM-dd'),
            end: format(end, 'yyyy-MM-dd')
          }
        }
      );
      
      if (response.data.success) {
        const transformedEvents = response.data.data.map(event => ({
          ...event,
          start: new Date(event.start),
          end: new Date(event.end),
        }));
        setEvents(transformedEvents);
        setChatbotName(response.data.chatbot_name || 'Events');
      }
    } catch (err) {
      setError('Failed to load events');
      console.error('Error fetching events:', err);
    } finally {
      setLoading(false);
    }
  }, [chatbotId, currentDate, view]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleNavigate = (direction) => {
    if (view === 'month') {
      setCurrentDate(direction === 'next' ? addMonths(currentDate, 1) : subMonths(currentDate, 1));
    } else if (view === 'week') {
      setCurrentDate(direction === 'next' ? addWeeks(currentDate, 1) : subWeeks(currentDate, 1));
    } else {
      setCurrentDate(direction === 'next' ? addDays(currentDate, 1) : subDays(currentDate, 1));
    }
  };

  const handleToday = () => setCurrentDate(new Date());

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setEventDetailDialog(true);
  };

  const eventStyleGetter = (event) => {
    const eventType = event.resource?.event_type || 'OTHER';
    const colors = eventTypeColors[eventType];
    
    return {
      style: {
        backgroundColor: colors.bg,
        borderLeft: `4px solid ${colors.border}`,
        color: colors.text,
        borderRadius: '4px',
        padding: '2px 6px',
        fontSize: '0.85em'
      }
    };
  };

  const EventComponent = ({ event }) => {
    const fillStatus = event.resource?.fill_status || 'open';
    const statusColors = fillStatusColors[fillStatus];
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {event.title}
        </Typography>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: statusColors.text, flexShrink: 0 }} />
      </Box>
    );
  };

  const getViewTitle = () => {
    if (view === 'month') return format(currentDate, 'MMMM yyyy');
    if (view === 'week') {
      const weekStart = startOfWeek(currentDate);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`;
    }
    return format(currentDate, 'EEEE, MMMM d, yyyy');
  };

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 3 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h5" fontWeight={700}>
              {chatbotName} Calendar
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton onClick={() => handleNavigate('prev')} size="small">
                <ArrowBackIcon />
              </IconButton>
              <Button size="small" startIcon={<TodayIcon />} onClick={handleToday}>
                Today
              </Button>
              <IconButton onClick={() => handleNavigate('next')} size="small">
                <ArrowForwardIcon />
              </IconButton>
              <Typography variant="subtitle1" sx={{ mx: 2, minWidth: 150, textAlign: 'center' }}>
                {getViewTitle()}
              </Typography>
              
              <ToggleButtonGroup
                value={view}
                exclusive
                onChange={(e, newView) => newView && setView(newView)}
                size="small"
              >
                <ToggleButton value="month"><MonthIcon fontSize="small" /></ToggleButton>
                <ToggleButton value="week"><WeekIcon fontSize="small" /></ToggleButton>
                <ToggleButton value="day"><DayIcon fontSize="small" /></ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
          
          {/* Legend */}
          <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">Availability:</Typography>
            {Object.entries(fillStatusColors).map(([status, colors]) => (
              <Box key={status} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colors.text }} />
                <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>{status}</Typography>
              </Box>
            ))}
          </Box>
        </Paper>

        {/* Calendar */}
        <Paper sx={{ p: 2, height: 600 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : (
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              style={{ height: '100%' }}
              view={view}
              onView={setView}
              date={currentDate}
              onNavigate={setCurrentDate}
              onSelectEvent={handleSelectEvent}
              eventPropGetter={eventStyleGetter}
              components={{ event: EventComponent }}
              toolbar={false}
              popup
            />
          )}
        </Paper>

        {/* Event Detail Dialog */}
        <Dialog open={eventDetailDialog} onClose={() => setEventDetailDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">{selectedEvent?.title}</Typography>
              <IconButton onClick={() => setEventDetailDialog(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedEvent && (
              <Box>
                <Box sx={{ mb: 2 }}>
                  <Chip label={selectedEvent.resource?.event_type?.replace('_', ' ')} size="small" sx={{ mr: 1 }} />
                  <Chip 
                    label={selectedEvent.resource?.spots_available > 0 ? `${selectedEvent.resource.spots_available} spots available` : 'Full'}
                    size="small"
                    color={selectedEvent.resource?.spots_available > 0 ? 'success' : 'error'}
                  />
                </Box>
                
                {selectedEvent.resource?.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {selectedEvent.resource.description}
                  </Typography>
                )}
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EventIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {format(selectedEvent.start, 'MMMM d, yyyy')}
                        {selectedEvent.resource?.event_end_date !== selectedEvent.resource?.event_start_date && 
                          ` - ${format(selectedEvent.end, 'MMMM d, yyyy')}`}
                      </Typography>
                    </Box>
                  </Grid>
                  {selectedEvent.resource?.event_start_time && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TimeIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {selectedEvent.resource.event_start_time} - {selectedEvent.resource.event_end_time}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationIcon fontSize="small" color="action" />
                      <Typography variant="body2">{selectedEvent.resource?.location}</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEventDetailDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default PublicEventCalendar;